import { getStudioApiUrl } from '../config/studio';

export type PublicAgentCard = {
  item_id: string;
  project_id: number;
  name: string;
  template: string;
  description: string;
  public_blurb: string;
  capabilities_shipped: number;
  total_capabilities: number;
  status: 'complete' | 'in_progress';
  featured: boolean;
};

export type PublicCertificate = {
  certificate_id: string;
  project_id: number;
  agent_name: string;
  title: string;
  issuer: string;
  issued_at: string;
  capabilities: string[];
  builder_display_name: string;
};

export type PublicPortfolio = {
  display_name: string;
  bio: string | null;
  avatar_url: string | null;
  tagline: string | null;
  headline_stat: string;
  portfolio_url: string;
  agent_cards: PublicAgentCard[];
  certificates: PublicCertificate[];
};

export type PublicCertificateVerify = {
  certificate_id: string;
  builder_display_name: string;
  builder_avatar_url: string | null;
  agent_name: string;
  title: string;
  issuer: string;
  issued_at: string;
  capabilities: Array<{ slug: string; title: string }>;
  portfolio_url: string | null;
  verify_url: string;
};

export type MeProfileAgentCard = PublicAgentCard & {
  visible: boolean;
};

export type MeProfileCertificate = PublicCertificate & {
  visible: boolean;
};

export type MeProfileResponse = {
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  tagline: string | null;
  profile_public: boolean;
  portfolio_url: string | null;
  certificate_access: boolean;
  agent_cards: MeProfileAgentCard[];
  certificates: MeProfileCertificate[];
};

export type PatchMeProfileInput = {
  display_name?: string;
  bio?: string | null;
  avatar_url?: string | null;
  tagline?: string | null;
  profile_public?: boolean;
};

export type PatchProfileItemInput = {
  visible?: boolean;
  featured?: boolean;
  public_blurb?: string;
};

async function publicGet<T>(path: string): Promise<T | null> {
  const res = await fetch(`${getStudioApiUrl()}${path}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`builder profile request failed (${res.status})`);
  return (await res.json()) as T;
}

async function authedJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${getStudioApiUrl()}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `builder profile request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export const builderProfileService = {
  getPublicPortfolio(username: string): Promise<PublicPortfolio | null> {
    return publicGet<PublicPortfolio>(`/public/portfolio/${encodeURIComponent(username)}`);
  },

  getPublicCertificate(id: string): Promise<PublicCertificateVerify | null> {
    return publicGet<PublicCertificateVerify>(`/public/cert/${encodeURIComponent(id)}`);
  },

  getMeProfile(): Promise<MeProfileResponse> {
    return authedJson<MeProfileResponse>('/me/profile', { method: 'GET' });
  },

  patchMeProfile(input: PatchMeProfileInput): Promise<MeProfileResponse> {
    return authedJson<MeProfileResponse>('/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  patchProfileItem(itemId: string, input: PatchProfileItemInput): Promise<MeProfileResponse> {
    return authedJson<MeProfileResponse>(`/me/profile/items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  patchCertificateVisibility(certId: string, visible: boolean): Promise<MeProfileResponse> {
    return authedJson<MeProfileResponse>(`/me/profile/certs/${encodeURIComponent(certId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ visible }),
    });
  },
};
